/**
 * Subscribes the external PA orders table to filter/search changes and runs Postgres-backed
 * searches after the initial load, keeping table context pagination in sync.
 */
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { SearchForMedicalPaOrdersFromPostgresParams } from "../../../../../api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import { useTablesContext } from "../../../../../context/tablesContextProvider";
import { RootState } from "../../../../../redux/store/store";
import { makeAlgoliaCallForExternalOrders } from "../apis/makeAlgoliaCallForExternalOrders";
import { ExternalTableState } from "./useExternalTableData";

const EXTERNAL_TABLE_NAME = "allMedicalPaOrdersForExternalByAlgolia";

/**
 * Side-effect hook that triggers a Postgres search whenever the filter payload
 * or auth status options change, but only after the initial data fetch is complete.
 * Also resets table context pagination state on each new search.
 *
 * @param filterPayload - The current search/filter parameters, or null if not yet computed
 * @param initialDataFetchingExecuted - Whether the initial data load has completed
 * @param setTableData - State setter for table row data
 * @param setExternalTableState - State setter for orders loading/data lifecycle
 * @param setPaginationResetCount - Incrementing setter to trigger pagination reset
 * @returns void
 */
export const useExternalFilterAndSearchEffect = (
  filterPayload: SearchForMedicalPaOrdersFromPostgresParams | null | undefined,
  initialDataFetchingExecuted: boolean,
  setTableData: React.Dispatch<React.SetStateAction<any[]>>,
  setExternalTableState: React.Dispatch<
    React.SetStateAction<ExternalTableState>
  >,
  setPaginationResetCount: React.Dispatch<React.SetStateAction<number>>,
) => {
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const { setTableDataForContext } = useTablesContext();
  const isFirstPayload = useRef(true);

  useEffect(() => {
    if (
      filterPayload &&
      initialDataFetchingExecuted &&
      authStatusOptions &&
      authStatusOptions.length > 0
    ) {
      if (isFirstPayload.current) {
        isFirstPayload.current = false;
        return;
      }

      makeAlgoliaCallForExternalOrders(
        filterPayload,
        authStatusOptions,
        setExternalTableState,
        setTableData,
        setPaginationResetCount,
      );
      setTableDataForContext(EXTERNAL_TABLE_NAME, {
        lastBatchFetched: 0,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterPayload, authStatusOptions]);
};
